import { ref, computed, onBeforeUnmount } from 'vue'
import { defineStore } from 'pinia'

export interface Notification {
  id: number;
  type: 'success' | 'warning' | 'info' | 'error';
  title: string;
  message: string;
  timestamp: number;
  duration: number;
}

export const useAppStore = defineStore('app', () => {
  // 状态
  const loading = ref(false)
  const loadingText = ref('')
  const sidebarCollapsed = ref(false)
  const theme = ref('light')
  const themeMode = ref<'light' | 'dark' | 'auto'>('light')
  const language = ref('zh-CN')
  const pageTitle = ref('')
  const breadcrumb = ref<string[]>([])
  const notifications = ref<Notification[]>([])
  const screenSize = ref<'xs' | 'sm' | 'md' | 'lg' | 'xl'>('lg')
  
  // 可用语言列表
  const availableLanguages = [
    { value: 'zh-CN', label: '简体中文', flag: '🇨🇳' },
    { value: 'en-US', label: 'English', flag: '🇺🇸' },
    { value: 'vi-VN', label: 'Tiếng Việt', flag: '🇻🇳' }
  ]
  
  // 加载计数
  const loadingCount = ref(0)

  // 版本计划（free/enterprise/custom）
  const currentPlan = ref<'free' | 'enterprise' | 'custom'>(
    (localStorage.getItem('currentPlan') as any) || 'free'
  )

  // Feature Flags（简易版，全局开关，localStorage持久化）
  const featureFlags = ref<Record<string, boolean>>((() => {
    try {
      const raw = localStorage.getItem('featureFlags')
      return raw ? JSON.parse(raw) : {
        reportsExportExcel: false,
        reportsExportPdf: false,
        aiPatrol: true,
        dashboardRealtimeWs: true,
      }
    } catch {
      return {
        reportsExportExcel: false,
        reportsExportPdf: false,
        aiPatrol: true,
        dashboardRealtimeWs: true,
      }
    }
  })())
  
  // 计算属性
  const isLoading = computed(() => loadingCount.value > 0 || loading.value)
  const isSidebarCollapsed = computed(() => sidebarCollapsed.value)
  const currentTheme = computed(() => theme.value)
  const currentThemeMode = computed(() => themeMode.value)
  const currentLanguage = computed(() => language.value)
  const currentPageTitle = computed(() => pageTitle.value)
  const currentBreadcrumb = computed(() => breadcrumb.value)
  const currentNotifications = computed(() => notifications.value)
  const currentScreenSize = computed(() => screenSize.value)
  const isMobile = computed(() => screenSize.value === 'xs' || screenSize.value === 'sm')
  
  // 获取当前语言信息
  const getCurrentLanguage = () => {
    return availableLanguages.find(l => l.value === language.value) || availableLanguages[0]
  }

  // Feature Flag 辅助
  const getFlag = (key: string): boolean => !!featureFlags.value[key]
  
  // 动作
  const setLoading = (status: boolean) => {
    loading.value = status
  }
  
  const incrementLoading = () => {
    loadingCount.value++
  }
  
  const decrementLoading = () => {
    if (loadingCount.value > 0) {
      loadingCount.value--
    }
  }
  
  const toggleSidebar = () => {
    sidebarCollapsed.value = !sidebarCollapsed.value
  }
  
  const setSidebarCollapsed = (collapsed: boolean) => {
    sidebarCollapsed.value = collapsed
  }
  
  // 设置版本计划
  const setPlan = (plan: 'free' | 'enterprise' | 'custom') => {
    currentPlan.value = plan
    try { window.localStorage.setItem('currentPlan', plan) } catch {}
  }

  // 设置 Feature Flag
  const setFeatureFlag = (key: string, enabled: boolean) => {
    featureFlags.value[key] = enabled
    try { localStorage.setItem('featureFlags', JSON.stringify(featureFlags.value)) } catch {}
  }
  
  const setTheme = (newTheme: 'light' | 'dark') => {
    theme.value = newTheme
    document.documentElement.setAttribute('data-theme', newTheme)
    window.localStorage.setItem('theme', newTheme)
  }
  
  const setThemeMode = (mode: 'light' | 'dark' | 'auto') => {
    themeMode.value = mode
    if (mode === 'auto') {
      window.localStorage.setItem('visuduo-theme-mode', 'auto')
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      setTheme(prefersDark ? 'dark' : 'light')
    } else {
      // 关闭自动模式
      window.localStorage.removeItem('visuduo-theme-mode')
      setTheme(mode)
    }
  }
  
  const setLanguage = (lang: string) => {
    if (availableLanguages.some(l => l.value === lang)) {
      language.value = lang
      window.localStorage.setItem('visuduo-language', lang)
    }
  }
  
  const setPageTitle = (title: string) => {
    pageTitle.value = title
    document.title = `${title} - ${import.meta.env.VITE_APP_TITLE}`
  }
  
  const setBreadcrumb = (items: string[]) => {
    breadcrumb.value = items
  }
  
  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp'>) => {
    const newNotification: Notification = {
      id: Date.now(),
      timestamp: Date.now(),
      duration: notification.duration || 3000,
      ...notification
    }
    notifications.value.push(newNotification)
  }
  
  const removeNotification = (id: number) => {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }
  
  const clearNotifications = () => {
    notifications.value = []
  }
  
  // 自动清理过期通知
  const cleanupExpiredNotifications = () => {
    const now = Date.now()
    notifications.value = notifications.value.filter(notification => 
      now - notification.timestamp <= notification.duration
    )
  }
  
  const handleResize = () => {
    const width = window.innerWidth
    if (width < 576) {
      screenSize.value = 'xs'
    } else if (width < 768) {
      screenSize.value = 'sm'
    } else if (width < 992) {
      screenSize.value = 'md'
    } else if (width < 1200) {
      screenSize.value = 'lg'
    } else {
      screenSize.value = 'xl'
    }
    
    // 在移动设备上自动折叠侧边栏
    if (isMobile.value) {
      sidebarCollapsed.value = true
    }
  }
  
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      // 页面重新可见时的处理
      console.log('页面重新可见')
    }
  }
  
  const initializeApp = () => {
    // 初始化主题
    const themeModeLs = window.localStorage.getItem('visuduo-theme-mode')
    const savedTheme = window.localStorage.getItem('theme') as 'light' | 'dark' | null
    if (themeModeLs === 'auto') {
      themeMode.value = 'auto'
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      setTheme(prefersDark ? 'dark' : 'light')
      // 跟随系统主题变化
      try {
        const media = window.matchMedia('(prefers-color-scheme: dark)')
        const handler = (e: MediaQueryListEvent) => {
          if (window.localStorage.getItem('visuduo-theme-mode') === 'auto') {
            setTheme(e.matches ? 'dark' : 'light')
          }
        }
        // 现代浏览器
        media.addEventListener?.('change', handler)
        // 兼容旧浏览器
        // @ts-ignore
        media.addListener?.(handler)
      } catch (e) {}
    } else if (savedTheme) {
      themeMode.value = savedTheme
      setTheme(savedTheme as 'light' | 'dark')
    } else {
      // 检测系统主题偏好
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      themeMode.value = prefersDark ? 'dark' : 'light'
      setTheme(prefersDark ? 'dark' : 'light')
    }
    
    // 初始化语言
    const savedLanguage = window.localStorage.getItem('visuduo-language')
    if (savedLanguage && availableLanguages.some(l => l.value === savedLanguage)) {
      language.value = savedLanguage
    }
    
    // 初始化屏幕尺寸
    handleResize()
    
    // 监听窗口大小变化
    window.addEventListener('resize', handleResize)
    
    // 启动通知清理定时器
    const cleanupInterval = setInterval(cleanupExpiredNotifications, 100)
    
    // 组件卸载时清理
    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize)
      clearInterval(cleanupInterval)
    })
    
    console.log('应用初始化完成')
  }
  
  // 错误处理
  const handleError = (error: any, context?: string) => {
    console.error(`错误发生在: ${context}`, error)
    
    addNotification({
      type: 'error',
      title: '操作失败',
      message: (error?.message || '发生未知错误') + (context ? `（${context}）` : ''),
      duration: 4000,
    })
  }
  
  // 成功提示
  const showSuccess = (message: string, title = '操作成功') => {
    addNotification({
      type: 'success',
      title,
      message,
      duration: 3000
    })
  }
  
  // 警告提示
  const showWarning = (message: string, title = '警告') => {
    addNotification({
      type: 'warning',
      title,
      message,
      duration: 4000
    })
  }
  
  // 信息提示
  const showInfo = (message: string, title = '提示') => {
    addNotification({
      type: 'info',
      title,
      message,
      duration: 3000
    })
  }
  
  return {
    // 状态
    loading,
    loadingText,
    sidebarCollapsed,
    theme,
    themeMode,
    language,
    pageTitle,
    breadcrumb,
    notifications,
    screenSize,
    availableLanguages,
     loadingCount,
     currentPlan,
     featureFlags,
    
    // 计算属性
    isLoading,
    isSidebarCollapsed,
    currentTheme,
    currentThemeMode,
    currentLanguage,
    currentPageTitle,
    currentBreadcrumb,
    currentNotifications,
    currentScreenSize,
    isMobile,
    
    // 动作
    setLoading,
    incrementLoading,
    decrementLoading,
    toggleSidebar,
     setSidebarCollapsed,
     setPlan,
     setFeatureFlag,
     setTheme,
     setThemeMode,
    setLanguage,
    setPageTitle,
    setBreadcrumb,
    addNotification,
    removeNotification,
    clearNotifications,
    handleResize,
    handleVisibilityChange,
    initializeApp,
    handleError,
    showSuccess,
    showWarning,
    showInfo,
    getCurrentLanguage,
    cleanupExpiredNotifications,
    getFlag,
  }
})

